import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  useFloating, autoUpdate, offset, flip, shift, size,
} from '@floating-ui/react-dom';
import PropTypes from 'prop-types';
import cx from 'classnames';
import containerQuery from '../../constants/containerQuery';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import SearchFilterDropdownContainer from './SearchFilterDropdownContainer';
import Button from '../Button';
import Close from '../../svg/icons/ic_close_20x20.svg';
import Chevron from '../../../../consumer_web/app/images/icons/chevron-left.svg';
import Icon from '../Icon';
import Link from '../Link';
import Loader from '../Loader';
import {
  CUISINE, LIST_ID, RESY_CREDIT_ELIGIBLE_ONLY, TABLE_AREA, VENUE_TYPE,
} from './searchFilterConstants';

import './SearchFilter.scss';
import handleCreatedByText from '../../utils/handleCreatedByText';
import ProfilePhoto from '../ProfilePhoto';
import transformListCreatedBy from '../../transformers/transformListCreatedBy';

const KEY_EVENTS = { enter: 'Enter' };
const NO_SCROLL_CLASS = 'no-scroll';

const SearchFilter = ({
  $rootScope,
  dropdownTitle,
  dropdownTranslationKey,
  isAuthenticated,
  isMobile,
  onFilterClick,
  title,
  translationKey,
  type,
  values,
  updateFilter,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const body = document.querySelector('body');
    if (isOpen || document.querySelector('.SearchFilter__Dropdown') !== null) {
      if (isMobile) body.classList.add(NO_SCROLL_CLASS);
    } else if (body.classList.contains(NO_SCROLL_CLASS)) body.classList.remove(NO_SCROLL_CLASS);
  }, [isOpen]);

  const isFloatingDropdown = () => window.matchMedia(`(min-width: ${containerQuery.md.minWidth}px)`).matches;

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    strategy: 'fixed',
    middleware: [
      offset(10),
      flip({ fallbackPlacements: ['top-end', 'bottom-start', 'top-start'] }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableHeight, elements }) {
          elements.floating.style.setProperty(
            '--popper-available-height',
            `${Math.max(availableHeight, 100)}px`
          );
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const toggleIsOpen = () => {
    setIsOpen((open) => !open);
  };

  let focusTrapTimeout = null;
  const clearFocusTrap = () => {
    clearTimeout(focusTrapTimeout);
  };
  const setFocusTrap = () => {
    focusTrapTimeout = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  const handleFilterClick = (item) => {
    toggleIsOpen();
    onFilterClick(item);
  };

  const renderNoListsMessage = () => {
    const learnMoreLink = 'https://blog.resy.com/2024/02/resy-lists/';
    return (
      <div className="SearchFilter__Lists-empty-state">
        <TranslationProvider
          translationKey="lists.filter"
        >
          <div className="SearchFilter__Lists-no-lists">
            Keep track of the restaurants you love, with Resy Lists.
          </div>
        </TranslationProvider>
        <Link
          href={learnMoreLink}
          target="_blank"
        >
          <TranslationProvider
            translationKey="learn_more"
          >
            <div className="SearchFilter__Lists-learn-more">
              Learn More
            </div>
          </TranslationProvider>
        </Link>
      </div>
    );
  };

  const renderCreatedBy = (createdBy) => {
    if (createdBy) {
      const { display, profilePicture } = createdBy;
      return (
        <div className="SearchFilter__created-by">
          <div className="SearchFilter__created-by-profile-pic">
            <ProfilePhoto url={profilePicture} size={20} />
          </div>
          <span>{handleCreatedByText(display)}</span>
        </div>
      );
    }
    return null;
  };

  const CuisineMenu = (cuisine) => {
    const { name, icon } = cuisine;
    return (
      <div className="SearchFilter__Cuisine-container">
        <div className="SearchFilter__Cuisine-icon">
          {icon}
        </div>
        <div className="SearchFilter__Cuisine-text">
          <div className="SearchFilter__Cuisine-name">
            {name}
          </div>
        </div>
      </div>
    );
  };

  const ListsMenu = (list) => {
    const {
      createdBy, count, image, name,
    } = list;
    return (
      <>
        <div className="SearchFilter__Lists-container">
          <div className="SearchFilter__Lists-image">
            {image}
          </div>
          <div className="SearchFilter__Lists-text">
            <div className="SearchFilter__Lists-name">
              <b>{name}</b>
            </div>
            <div className="SearchFilter__Lists-count">
              {count}
            </div>
            {createdBy
              && renderCreatedBy(transformListCreatedBy(createdBy))}
          </div>
        </div>
      </>
    );
  };

  const SeatingMenu = (seating) => {
    const { name, icon } = seating;
    return (
      <div className="SearchFilter__Seating-container">
        <div className="SearchFilter__Seating-icon">
          {icon}
        </div>
        <div className="SearchFilter__Seating-text">
          <div className="SearchFilter__Seating-name">
            {name}
          </div>
        </div>
      </div>
    );
  };

  const renderDropdownContent = (item) => {
    switch (type) {
      case 'lists':
        return ListsMenu(item);
      case 'seating':
        return SeatingMenu(item);
      default:
        return CuisineMenu(item);
    }
  };

  const renderPill = () => {
    if (type === 'resy_credit') {
      const isActive = new URL(window.location.href).searchParams.get('facet')
        === `${RESY_CREDIT_ELIGIBLE_ONLY}:true`;
      return (
        <Button
          aria-pressed={isActive}
          aria-label={isActive
            ? getTranslation({
              translationKey: 'clear_filter',
              translationValues: { filterType: title },
              fallback: `Clear filter: ${title}`,
            })
            : getTranslation({
              translationKey: 'apply_filter_resy_credit',
              fallback: 'Apply filter: Resy Credit',
            })}
          className={cx(
            'SearchFilter__button',
            'SearchFilter__button--resy-credit',
            { 'SearchFilter__button-has-clear': isActive }
          )}
          data-test-id="search-filter-resy-credit-toggle"
          onMouseDown={clearFocusTrap}
          onClick={() => onFilterClick()}
          onTouchStart={clearFocusTrap}
          tabIndex={0}
          type="button"
          variant="no-style"
        >
          <TranslationProvider translationKey={translationKey}>
            <div className="SearchFilter__button-text">{title}</div>
          </TranslationProvider>
        </Button>
      );
    }

    const url = new URL(window.location.href);
    const params = url.searchParams;
    const facet = params.get('facet');
    const facetKey = facet && facet.split(':')[0];
    const facetToTypeMap = {
      cuisine: 'cuisines',
      list_id: 'lists',
      table_area: 'seating',
      venue_type: 'type',
    };
    const supportedFacets = [LIST_ID, CUISINE, TABLE_AREA, VENUE_TYPE];
    if (
      supportedFacets.some((facetItem) => facetItem === facetKey)
      && type === facetToTypeMap[facetKey]
    ) {
      return (
        <Button
          aria-expanded={isOpen}
          aria-label={getTranslation({
            translationKey: 'clear_filter',
            translationValues: { filterType: title },
            fallback: `Clear filter: ${title}`,
          })}
          className="SearchFilter__button SearchFilter__button-has-clear"
          data-test-id="search-filter-pill-button-clear"
          onMouseDown={clearFocusTrap}
          onClick={updateFilter}
          onTouchStart={clearFocusTrap}
          variant="no-style"
        >
          <TranslationProvider translationKey={translationKey}>
            <div className="SearchFilter__button-text">{title}</div>
          </TranslationProvider>
          <Icon
            name="Close"
            component={Close}
            className="SearchFilter__close"
            height="12px"
            width="12px"
          />
        </Button>
      );
    }
    return (
      <Button
        aria-expanded={isOpen}
        aria-label={getTranslation({
          translationKey: 'expand_filter_menu',
          translationValues: { filterTitle: title },
          fallback: `Expand filter menu: ${title}`,
        })}
        className="SearchFilter__button"
        data-test-id="search-filter-pill-button"
        onMouseDown={clearFocusTrap}
        onClick={toggleIsOpen}
        onTouchStart={clearFocusTrap}
        tabIndex={0}
        variant="no-style"
      >
        <TranslationProvider translationKey={translationKey}>
          <div className="SearchFilter__button-text">{title}</div>
        </TranslationProvider>
        <Icon
          name="Chevron"
          component={Chevron}
          className={cx('SearchFilter__chevron', {
            open: isOpen,
          })}
          height="10px"
          width="10px"
        />
      </Button>
    );
  };

  const onLogin = () => {
    $rootScope.$broadcast('MODAL', 'signin');
  };

  const renderDropdown = () => {
    const isVenueTypeFilter = type === 'type';
    const dropdownHeaderTitle = isVenueTypeFilter ? (dropdownTitle || title) : title;
    const dropdownHeaderTranslationKey = isVenueTypeFilter
      ? (dropdownTranslationKey || translationKey)
      : translationKey;

    if (isOpen && !values) {
      return (
        <SearchFilterDropdownContainer
          title={dropdownHeaderTitle}
          toggleIsOpen={toggleIsOpen}
          translationKey={dropdownHeaderTranslationKey}
          setFocusTrap={setFocusTrap}
        >
          <Loader containerHeight="100px" />
        </SearchFilterDropdownContainer>
      );
    }

    // Logged out state
    if (isOpen && values) {
      const itemsLength = values.length;
      if (type === 'lists' && itemsLength === 0 && !isAuthenticated) {
        return (
          <SearchFilterDropdownContainer
            title={title}
            toggleIsOpen={toggleIsOpen}
            translationKey={translationKey}
            setFocusTrap={setFocusTrap}
          >
            <div className="SearchFilter__Lists-logged-out">
              <div className="SearchFilter__Lists-login-button">
                <TranslationProvider translationKey="login">
                  <Button
                    variant="login"
                    onClick={onLogin}
                    data-test-id="search-filter-button-login"
                  >
                    Log in
                  </Button>
                </TranslationProvider>
              </div>
              {renderNoListsMessage()}
            </div>
          </SearchFilterDropdownContainer>
        );
      }

      return (
        <SearchFilterDropdownContainer
          title={dropdownHeaderTitle}
          toggleIsOpen={toggleIsOpen}
          translationKey={dropdownTranslationKey || translationKey}
          setFocusTrap={setFocusTrap}
        >
          <div className="SearchFilter__Dropdown__container">
            {values.map((item) => (
              <Button
                variant="no-style"
                className="SearchFilter__Dropdown__Item"
                data-test-id="search-filter-item"
                key={`${type}-${item.name}`}
                onClick={() => handleFilterClick(item)}
                onKeyUp={(e) => {
                  if (e.key === KEY_EVENTS.enter) {
                    handleFilterClick(item);
                  }
                }}
              >
                {renderDropdownContent(item)}
              </Button>
            ))}
          </div>
          {(itemsLength === 1 && type === 'lists') && renderNoListsMessage()}
        </SearchFilterDropdownContainer>
      );
    }
    return null;
  };

  const renderDropdownWithPortal = () => {
    const dropdown = renderDropdown();
    if (dropdown && isFloatingDropdown()) {
      return ReactDOM.createPortal(
        <div
          ref={refs.setFloating}
          className="SearchFilter__dropdown-portal"
          style={floatingStyles}
        >
          {dropdown}
        </div>,
        document.body
      );
    }
    return dropdown;
  };

  return (
    <div className="SearchFilter" ref={refs.setReference}>
      {renderPill()}
      {renderDropdownWithPortal()}
    </div>
  );
};

SearchFilter.propTypes = {
  $rootScope: PropTypes.shape({
    $apply: PropTypes.func.isRequired,
    $broadcast: PropTypes.func.isRequired,
  }).isRequired,
  dropdownTitle: PropTypes.string,
  dropdownTranslationKey: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  isMobile: PropTypes.bool,
  onFilterClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  translationKey: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  updateFilter: PropTypes.func.isRequired,
};

SearchFilter.defaultProps = {
  dropdownTitle: undefined,
  dropdownTranslationKey: undefined,
  isAuthenticated: false,
  isMobile: false,
  values: undefined,
};

SearchFilter.displayName = 'SearchFilter';
export default SearchFilter;
